import React, { useEffect, useState } from "react";
import GenerateCategoryButtons from "../../../widgets/generateCategoryButtons";
import { GenerateHtmlMarkup } from "../../../widgets/generateHtmlMarkup";
import GenerateIssueButton from "../../../widgets/generateIssueButton";
import GenerateShareButtons from "../../../widgets/generateShareButtons";
import getArticleAttribute from "../../../modules/library/articleViewer/manage_attributes/getArticleAttribute";
import getSpecialAttribute from "../../../modules/library/articleViewer/manage_attributes/getSpecialAttribute";
import { getExtractedElements, extractGalleryImages } from "../../../modules/library/articleViewer/content_updaters/findElement";
import { GenerateArticleBodyContent } from "../../../modules/library/articleViewer/content_updaters/generateArticleBodyContent";
import { updateArticleImages } from "../../../modules/library/articleViewer/content_updaters/updateArticleImages";
import { clone } from "../../../utils/objectUtils";
import ImageSlider from "../../../widgets/imageSlider/index";

/**
 * Attributes to extract from the article content and moved to a new position.
 * created as array, as order matters
 *
 * @type {({name: string, id: string, duplicate: boolean, element: string}|{prefix: {i18n: string}, name: string, class: string, element: string}|{name: string, element: string}|{name: string, element: string})[]}
 */
const elementsAttributes = [
	{ name: 'roofHeader', element: ".roofHeader", id: "roof-header-first", duplicate: true },
	{ name: 'roofHeaderScreenReader', element: ".roofHeader", class: "screen-reader", prefix: { i18n: "article.roofHeader.prefix" } },
	{ name: 'subtitle', element: ".subtitle" },
	{ name: 'byline', element: ".byline" },
	{ name: 'sectionHeader', element: ".sectionHeader" },
];

const maxWidth = 50000;

const loadImage = (setSliderDimensions, setImageHeights, setImageWidths, imageUrls) => {
	const dimensions = {height: 0, width: 0, count: 0};
	const heights = [];
	const widths = [];
	imageUrls.map((imageUrl, index, array) => {
		console.log(index, "loadImage", imageUrl);
		const img = new Image();
		img.id = index;
		img.src = imageUrl;
		img.onload = () => {
			console.log(img.id, dimensions.count, "onload", img.src);
			dimensions.count++;
			let scaleFactor = 1;
			const idx = parseInt(img.id);
			heights[idx] = img.height;
			widths[idx] = img.width;
			if (img.width > maxWidth) {
				scaleFactor = img.width/maxWidth;
			}
			const newHeight = scaleFactor > 1 ? img.height / scaleFactor : img.height;
			const newWidth = scaleFactor > 1 ? img.width / scaleFactor : img.width;
			const curHeight = dimensions.height;
			const curWidth = dimensions.width;
			if (newHeight > curHeight) {
				dimensions.height = newHeight;
			}
			if (newWidth > curWidth) {
				dimensions.width = newWidth;
			}
			if (dimensions.count === array.length) {
				setSliderDimensions(dimensions);
			}
		};
		img.onerror = (err) => {
			console.log(img.id, "img error");
			console.error(err);
		};
		setImageHeights(heights);
		setImageWidths(widths);
		return heights;
	});
};

/**
 * React code to generate html for "default" template
 *
 * @param params
 *     article article object
 *     moduleProps props from calling module
 *
 * @returns {JSX.Element}
 * @constructor
 */
const GenerateGalleryArticle = (params) => {
	params = Object.assign({
		article: {},
		template: 'gallery',
		moduleProps: {}
	}, params);

	const [sliderDimensions, setSliderDimensions] = useState({ height: 0, width: 0 });
	const [imageHeights, setImageHeights] = useState([]);
	const [imageWidths, setImageWidths] = useState([]);
	let imageUrls;

	useEffect(() => {
		loadImage(setSliderDimensions, setImageHeights, setImageWidths, imageUrls);
	}, [imageUrls]);

	// make a clone of article so we don't accidentally change anything for the caller
	const article = clone(params.article);
	const title = getArticleAttribute({ article: article, attribute: "title" }).value;
	const categories = getSpecialAttribute({ article: article, attribute: "categories" }).value;
	const className = 'gallery scrollContainer ' + categories;
	article.content = updateArticleImages({ articleMarkup: article.content, template: params.template });

	// gets object of extracted elements and modified article content
	const extractedElements = getExtractedElements({ article: article, elementsAttributes: elementsAttributes });

	const galleryElements = extractGalleryImages({ articleMarkup: extractedElements.articleMarkup, template: params.template });
	imageUrls = galleryElements.urls;
	const slides = galleryElements.images.map((image) => {
		return updateArticleImages({ articleMarkup: image, template: params.template });
	});

	const articleMarkup = updateArticleImages({ articleMarkup: galleryElements.markup, template: params.template });

	const shareAttributes = {
		elfSightCode: params.moduleProps.hasOwnProperty('elfSightCode') ? params.moduleProps.elfSightCode : '',
		shareTitle: article.title,
		isApp: params.moduleProps.app,
		platform: params.moduleProps.platform,
		moduleName: params.moduleProps.name,
		displayShareButtons: params.moduleProps.displayShareButtons,
		storageKey: params.moduleProps.storageKey
	};

	const containerStyles = {
		height: `${sliderDimensions.height}px`,
	};

	return (
		<div data-templatename="Gallery" className={className}>
			<div className="contentContainer gtxcelDefaultTemplate">
				<GenerateHtmlMarkup htmlMarkup={extractedElements.elements.sectionHeader} />
				<div className="articleBodyContainer">
					<header className="header">
						<GenerateHtmlMarkup htmlMarkup={extractedElements.elements.roofHeader} />
						<GenerateIssueButton issueUrl={article.issueUrl} issueName={article.issueName} articleId={article.articleId} articleTitle={article.title} moduleProps={params.moduleProps} />
						{params.moduleProps.canDisplayShareButtons ? <GenerateShareButtons {...shareAttributes} /> : ''}
						<h1 className="mainTitle" tabIndex="-1">
							<GenerateHtmlMarkup htmlMarkup={title} />
							<GenerateHtmlMarkup htmlMarkup={extractedElements.elements.roofHeaderScreenReader} />
						</h1>
						<GenerateHtmlMarkup htmlMarkup={extractedElements.elements.subtitle} />
						<GenerateHtmlMarkup htmlMarkup={extractedElements.elements.byline} />
					</header>
					<GenerateCategoryButtons categories={article.categories} articleId={article.articleId} articleTitle={article.title} moduleProps={params.moduleProps} />
					<div className="article-gallery">
						<div id="imageSlider" style={containerStyles} >
							<ImageSlider slides={slides} sliderDimensions={sliderDimensions} imageHeights={imageHeights} imageWidths={imageWidths}/>
						</div>
					</div>
					<div class="gallery-clear"/>
					<div className=" articleBody">
						<article>
							<GenerateArticleBodyContent templateName={params.template} articleMarkup={articleMarkup} article={article} moduleProps={params.moduleProps} />
						</article>
					</div>
				</div>
			</div>
		</div>
	);

};
export default GenerateGalleryArticle;
